"use client"

import { useState, useEffect, useRef } from "react"
import { useAuth } from "../../../contexts/AuthContext"
import { API_BASE_URL } from "../../../config/api"
import { createPortal } from "react-dom"

// Inline SVG Icon Components for Reliability
const SearchIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
)

const FilterIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
)

const AttachIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
  </svg>
)

const SendIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
)

const ClipIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
)

const BackIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
  </svg>
)

const DownloadIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
)

const TrashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
)

const Support = () => {
  const {
    user,
    createSupportTicket,
    getSupportTickets,
    getSupportTicket,
    updateSupportTicket,
    updateSupportTicketPriority,
    reopenSupportTicket,
    assignSupportTicket,
    addSupportTicketResponse,
    getSupportTicketStats,
    exportSupportTickets,
    markSupportNotificationsRead,
    getAllUsers
  } = useAuth()

  // State Management
  const [tickets, setTickets] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    highPriority: 0,
    today: 0
  })

  // Pagination & Filters
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterPriority, setFilterPriority] = useState("all")
  const [filterCategory, setFilterCategory] = useState("all")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)

  // Details & Modals
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [assigneeEmail, setAssigneeEmail] = useState("")
  const [statusRemark, setStatusRemark] = useState("")
  const [staffList, setStaffList] = useState([])

  // Form Creation
  const [ticketSubject, setTicketSubject] = useState("")
  const [ticketCategory, setTicketCategory] = useState("general")
  const [ticketPriority, setTicketPriority] = useState("medium")
  const [ticketDescription, setTicketDescription] = useState("")
  const [selectedFiles, setSelectedFiles] = useState([])

  // Response/Chat Form
  const [replyText, setReplyText] = useState("")
  const [isInternalNote, setIsInternalNote] = useState(false)
  const [replyFiles, setReplyFiles] = useState([])

  const [statsLoading, setStatsLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sendingReply, setSendingReply] = useState(false)
  const [totalTickets, setTotalTickets] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")

  const messagesEndRef = useRef(null)

  const isAdmin = ["superadmin", "admin"].includes(user?.role)
  const isSupportStaff = user?.role === "support"
  const isAgentOrUser = !isAdmin && !isSupportStaff

  // Fetch staff list for assignment dropdown
  useEffect(() => {
    if (isAdmin) {
      const fetchStaff = async () => {
        try {
          const res = await getAllUsers()
          if (res.success) {
            const staff = res.data.filter((u) => ["admin", "superadmin", "support"].includes(u.role))
            setStaffList(staff)
          }
        } catch (error) {
          console.error("Error fetching staff list:", error)
        }
      }
      fetchStaff()
    }
  }, [isAdmin])

  // Load ticket statistics and tickets list
  useEffect(() => {
    loadStats()
    loadTickets(1)
    // Mark support notifications read when opening the page
    if (markSupportNotificationsRead) {
      markSupportNotificationsRead().catch((e) => console.log(e))
    }
  }, [filterStatus, filterPriority, filterCategory, startDate, endDate])

  // Scroll to bottom when responses/messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [selectedTicket?.responses])

  const loadStats = async () => {
    setStatsLoading(true)
    try {
      if (getSupportTicketStats) {
        const statsData = await getSupportTicketStats()
        if (statsData) setStats(statsData)
      }
    } catch (error) {
      console.error("Error loading support stats:", error)
    } finally {
      setStatsLoading(false)
    }
  }

  const loadTickets = async (page = 1) => {
    setLoading(true)
    try {
      const params = {
        page,
        limit: 10,
        status: filterStatus,
        priority: filterPriority,
        category: filterCategory,
        startDate,
        endDate,
        search: searchTerm,
      }
      const res = await getSupportTickets(params)
      if (res && res.success) {
        setTickets(res.data || [])
        setCurrentPage(res.pagination.currentPage || 1)
        setTotalPages(res.pagination.totalPages || 1)
        setTotalTickets(res.pagination.totalTickets || 0)
      }
    } catch (error) {
      console.error("Error loading support tickets:", error)
      setTickets([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    loadTickets(1)
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setFilterStatus("all")
    setFilterPriority("all")
    setFilterCategory("all")
    setStartDate("")
    setEndDate("")
    setCurrentPage(1)
  }

  // Load specific ticket detail
  const loadTicketDetail = async (ticketId) => {
    setDetailLoading(true)
    try {
      const res = await getSupportTicket(ticketId)
      if (res && res.success) {
        setSelectedTicket(res.data)
      }
    } catch (error) {
      console.error("Error fetching support ticket details:", error)
    } finally {
      setDetailLoading(false)
    }
  }

  // Create Ticket Form Handler
  const handleCreateTicketSubmit = async (e) => {
    e.preventDefault()
    if (!ticketSubject.trim() || !ticketDescription.trim()) return

    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append("subject", ticketSubject.trim())
      formData.append("category", ticketCategory)
      formData.append("priority", ticketPriority)
      formData.append("message", ticketDescription.trim())

      selectedFiles.forEach((file) => {
        formData.append("attachments", file)
      })

      const res = await createSupportTicket(formData)
      if (res && res.success) {
        setTicketSubject("")
        setTicketCategory("general")
        setTicketPriority("medium")
        setTicketDescription("")
        setSelectedFiles([])
        setShowCreateModal(false)
        loadStats()
        loadTickets(1)
      }
    } catch (error) {
      console.error("Error submitting ticket:", error)
    } finally {
      setSubmitting(false)
    }
  }

  // Reply Chat Handler
  const handleReplySubmit = async (e) => {
    e.preventDefault()
    if (!replyText.trim() && replyFiles.length === 0) return

    setSendingReply(true)
    try {
      const formData = new FormData()
      formData.append("message", replyText.trim())
      formData.append("isInternal", isInternalNote)

      replyFiles.forEach((file) => {
        formData.append("attachments", file)
      })

      const res = await addSupportTicketResponse(selectedTicket._id, formData)
      if (res && res.success) {
        setReplyText("")
        setReplyFiles([])
        setIsInternalNote(false)
        await loadTicketDetail(selectedTicket.ticketId)
      }
    } catch (error) {
      console.error("Error posting chat reply:", error)
    } finally {
      setSendingReply(false)
    }
  }

  // Update Status directly
  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return
    try {
      const res = await updateSupportTicket(selectedTicket._id, {
        status: newStatus,
        remark: statusRemark || `Status set to ${newStatus.toUpperCase()}`
      })
      if (res && res.success) {
        setStatusRemark("")
        await loadTicketDetail(selectedTicket.ticketId)
        loadStats()
        loadTickets(currentPage)
      }
    } catch (error) {
      console.error("Error changing ticket status:", error)
    }
  }

  // Update Priority
  const handlePriorityChange = async (newPriority) => {
    if (!selectedTicket) return
    try {
      const res = await updateSupportTicketPriority(selectedTicket._id, newPriority)
      if (res && res.success) {
        await loadTicketDetail(selectedTicket.ticketId)
        loadTickets(currentPage)
      }
    } catch (error) {
      console.error("Error changing ticket priority:", error)
    }
  }

  // Assign Ticket
  const handleAssignSubmit = async (e) => {
    e.preventDefault()
    if (!selectedTicket || !assigneeEmail.trim()) return

    try {
      const res = await assignSupportTicket(selectedTicket._id, assigneeEmail.trim())
      if (res && res.success) {
        setAssigneeEmail("")
        await loadTicketDetail(selectedTicket.ticketId)
        loadTickets(currentPage)
      }
    } catch (error) {
      console.error("Error assigning ticket:", error)
    }
  }

  // Reopen Ticket
  const handleReopenTicket = async () => {
    if (!selectedTicket) return
    try {
      const res = await reopenSupportTicket(selectedTicket._id)
      if (res && res.success) {
        await loadTicketDetail(selectedTicket.ticketId)
        loadStats()
        loadTickets(currentPage)
      }
    } catch (error) {
      console.error("Error reopening ticket:", error)
    }
  }

  // Export tickets to Excel
  const handleExportExcel = async () => {
    try {
      const filters = {
        status: filterStatus,
        priority: filterPriority,
        category: filterCategory,
        startDate,
        endDate,
        search: searchTerm,
      }
      await exportSupportTickets(filters)
    } catch (error) {
      console.error("Failed to export support tickets:", error)
    }
  }

  // Badges CSS Mappings
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200"
      case "pending":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200 border-orange-200"
      case "in_progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200"
      case "resolved":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 border-purple-200"
      case "closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-200 border-gray-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-200"
    }
  }

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case "low":
        return "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800"
      case "medium":
        return "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800"
      case "high":
        return "bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
      case "urgent":
        return "bg-red-900 text-red-50 border-red-950 dark:bg-red-950 dark:text-red-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-100"
    }
  }

  // Handle files adding
  const handleFileChange = (e, target) => {
    const files = Array.from(e.target.files)
    if (target === "create") {
      setSelectedFiles((prev) => [...prev, ...files])
    } else {
      setReplyFiles((prev) => [...prev, ...files])
    }
  }

  const removeFile = (index, target) => {
    if (target === "create") {
      setSelectedFiles((prev) => prev.filter((_, idx) => idx !== index))
    } else {
      setReplyFiles((prev) => prev.filter((_, idx) => idx !== index))
    }
  }

  // Initials for avatar
  const getInitials = (userObj) => {
    if (!userObj) return "?"
    const name = userObj.username || userObj.email || "?"
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Support Helpdesk</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Radiant & Professional Ticket Management System</p>
        </div>
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={handleExportExcel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-750 transition"
            >
              Export Excel
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent bg-green-600 text-white text-sm font-medium rounded-xl shadow-sm hover:bg-green-700 transition"
          >
            Create Ticket
          </button>
        </div>
      </div>

      {/* Stats Cards Dashboard (Visible to both, admin sees global metrics) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
        {[
          { label: "Total", value: stats.total, color: "text-gray-900 dark:text-white" },
          { label: "Open", value: stats.open, color: "text-green-600 dark:text-green-400" },
          { label: "In Progress", value: stats.inProgress || 0, color: "text-blue-600 dark:text-blue-400" },
          { label: "Pending", value: stats.pending, color: "text-orange-600 dark:text-orange-400" },
          { label: "Resolved", value: stats.resolved || 0, color: "text-purple-600 dark:text-purple-400" },
          { label: "Closed", value: stats.closed, color: "text-red-500" },
          { label: "High Priority", value: stats.highPriority || 0, color: "text-red-700 dark:text-red-300 font-extrabold" },
          { label: "Today", value: stats.today || 0, color: "text-teal-600" },
        ].map((s, idx) => (
          <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-150 dark:border-gray-750 shadow-sm flex flex-col justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">{s.label}</span>
            {statsLoading ? (
              <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md mt-2"></div>
            ) : (
              <span className={`text-2xl font-bold mt-2 ${s.color}`}>{s.value}</span>
            )}
          </div>
        ))}
      </div>

      {/* Main Ticket Workspace View */}
      {!selectedTicket ? (
        <div className="space-y-4">
          {/* Controls, Filters & Search bar */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-150 dark:border-gray-750 shadow-sm flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 items-center justify-between">
            <form onSubmit={handleSearchSubmit} className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search ticket ID, subject..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm focus:ring-green-500 focus:border-green-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </form>

            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <select
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs rounded-xl focus:ring-green-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>

              <select
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs rounded-xl focus:ring-green-500"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>

              <select
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs rounded-xl focus:ring-green-500"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="general">General</option>
                <option value="other">Other</option>
              </select>

              <input
                type="date"
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs rounded-xl"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                type="date"
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs rounded-xl"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />

              <button
                onClick={handleClearFilters}
                className="px-3 py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-650"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Tickets List Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-750 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-750">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticket ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-750">
                  {loading ? (
                    // Loading skeleton rows
                    Array.from({ length: 5 }).map((_, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div></td>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div></td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                        <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div></td>
                      </tr>
                    ))
                  ) : tickets.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 8 : 7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col items-center">
                          <ClipIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                          <p className="font-semibold text-lg">No tickets found</p>
                          <p className="text-sm">Try adjusting your filter settings or create a new support ticket.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tickets.map((t) => {
                      const hasUnread = (isAdmin || isSupportStaff) ? t.unreadForAdmin : t.unreadForUser
                      return (
                        <tr key={t._id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 transition ${hasUnread ? "bg-green-50/20 font-semibold" : ""}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white flex items-center">
                            {hasUnread && (
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 flex-shrink-0 animate-pulse" />
                            )}
                            #{t.ticketId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{t.subject}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 capitalize">{t.category || "General"}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(t.status)}`}>
                              {t.status.toUpperCase().replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(t.priority)}`}>
                              {t.priority.toUpperCase()}
                            </span>
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {t.createdBy ? t.createdBy.email : "Unknown"}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(t.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => loadTicketDetail(t.ticketId)}
                              className="text-green-600 hover:text-green-800 dark:text-green-400 font-semibold"
                            >
                              View Ticket
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-150 dark:border-gray-750 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Showing page <strong className="font-semibold">{currentPage}</strong> of <strong className="font-semibold">{totalPages}</strong> ({totalTickets} total tickets)
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => loadTickets(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadTickets(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Ticket Detail split workspace views */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Back button */}
          <div className="lg:col-span-3">
            <button
              onClick={() => {
                setSelectedTicket(null)
                loadStats()
                loadTickets(currentPage)
              }}
              className="inline-flex items-center text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
            >
              <BackIcon className="w-4 h-4 mr-2" /> Back to Tickets List
            </button>
          </div>

          {/* Left panel: Info Panel Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-750 shadow-sm p-6 space-y-6">
            <div>
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Ticket details</h2>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-1">#{selectedTicket.ticketId}</h1>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 font-bold uppercase block">Subject</label>
                <span className="text-sm font-medium text-gray-900 dark:text-white block mt-0.5">{selectedTicket.subject}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 font-bold uppercase block">Category</label>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize block mt-0.5">{selectedTicket.category || "general"}</span>
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-bold uppercase block">Created At</label>
                  <span className="text-sm font-medium text-gray-900 dark:text-white block mt-0.5">
                    {new Date(selectedTicket.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 font-bold uppercase block">Status</label>
                  {isAdmin || isSupportStaff ? (
                    <select
                      className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-xs"
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border mt-1 ${getStatusBadge(selectedTicket.status)}`}>
                      {selectedTicket.status.toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-bold uppercase block">Priority</label>
                  {isAdmin || isSupportStaff ? (
                    <select
                      className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-xs"
                      value={selectedTicket.priority}
                      onChange={(e) => handlePriorityChange(e.target.value)}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border mt-1 ${getPriorityBadge(selectedTicket.priority)}`}>
                      {selectedTicket.priority.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 font-bold uppercase block">User Information</label>
                <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-xl border border-gray-100 dark:border-gray-750 mt-1">
                  <p className="text-xs text-gray-900 dark:text-white font-medium block">
                    {selectedTicket.createdBy?.username || "Client User"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 block mt-0.5">
                    {selectedTicket.createdBy?.email}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 font-bold uppercase block">Assigned Staff</label>
                {isAdmin ? (
                  <form onSubmit={handleAssignSubmit} className="flex space-x-1.5 mt-1">
                    <select
                      className="flex-1 px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-xs font-semibold"
                      value={assigneeEmail}
                      onChange={(e) => setAssigneeEmail(e.target.value)}
                      required
                    >
                      <option value="">Select staff...</option>
                      {staffList.map((s) => (
                        <option key={s._id} value={s.email}>
                          {s.username} ({s.email}) - {s.role.toUpperCase()}
                        </option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      className="px-2.5 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 whitespace-nowrap"
                    >
                      Assign
                    </button>
                  </form>
                ) : (
                  <span className="text-sm font-medium text-gray-900 dark:text-white block mt-0.5">
                    {selectedTicket.assignedTo ? selectedTicket.assignedTo.email : "Unassigned"}
                  </span>
                )}
              </div>

              {/* Status transition log timeline */}
              <div>
                <label className="text-xs text-gray-400 font-bold uppercase block mb-2">History & Timeline</label>
                <div className="space-y-3 relative border-l border-gray-200 dark:border-gray-750 pl-4 mt-2">
                  {selectedTicket.statusHistory && selectedTicket.statusHistory.length > 0 ? (
                    selectedTicket.statusHistory.map((h, index) => (
                      <div key={index} className="relative text-xs">
                        <div className="absolute -left-6 top-1.5 w-3.5 h-3.5 bg-green-500 rounded-full border-4 border-white dark:border-gray-800" />
                        <p className="font-semibold text-gray-900 dark:text-white">Status: {h.status.toUpperCase()}</p>
                        <p className="text-gray-500 dark:text-gray-400 leading-normal">{h.remark}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{new Date(h.updatedAt).toLocaleString()}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-gray-400">Created: {new Date(selectedTicket.createdAt).toLocaleString()}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right panel: Chat Conversation Workspace */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-750 shadow-sm overflow-hidden flex flex-col h-[650px]">
            {/* Ticket Header & Status Options */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-250 dark:border-gray-750 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center font-bold text-green-600">
                  {getInitials(selectedTicket.createdBy)}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">{selectedTicket.subject}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Category: {selectedTicket.category || "General"}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                {selectedTicket.status === "closed" ? (
                  <button
                    onClick={handleReopenTicket}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg shadow"
                  >
                    Reopen Ticket
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatusChange("closed")}
                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-bold rounded-lg shadow"
                  >
                    Close Ticket
                  </button>
                )}
              </div>
            </div>

            {/* Chat Bubble Flow */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 dark:bg-gray-900/10">
              {/* Initial Ticket Message */}
              {(() => {
                const isInitialAuthor = selectedTicket.createdBy?._id === user?._id || selectedTicket.createdBy === user?._id
                return (
                  <div
                    className={`flex space-x-3 items-start max-w-lg ${
                      isInitialAuthor ? "ml-auto flex-row-reverse space-x-reverse" : "mr-auto"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                      {getInitials(selectedTicket.createdBy)}
                    </div>
                    <div
                      className={`rounded-2xl p-4 text-sm ${
                        isInitialAuthor
                          ? "bg-green-600 text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      }`}
                    >
                      <p className="whitespace-pre-line">{selectedTicket.message}</p>
                      
                      {/* Initial Attachments */}
                      {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <p className={`text-[10px] font-bold uppercase ${isInitialAuthor ? "text-green-200" : "text-gray-400"}`}>
                            Attachments:
                          </p>
                          {selectedTicket.attachments.map((file, fileIdx) => (
                            <a
                              key={fileIdx}
                              href={`${selectedTicket.url || API_BASE_URL}${file.url}`}
                              target="_blank"
                              rel="noreferrer"
                              className={`inline-flex items-center px-2 py-1 border rounded-lg text-xs mt-1 mr-1 ${
                                isInitialAuthor
                                  ? "bg-green-700 border-green-800 text-white hover:bg-green-800"
                                  : "bg-white dark:bg-gray-800 border-gray-250 dark:border-gray-600 text-green-600"
                              }`}
                            >
                              <DownloadIcon className="mr-1 w-3.5 h-3.5" /> {file.originalName}
                            </a>
                          ))}
                        </div>
                      )}
                      <span className={`text-[10px] block text-right mt-1.5 ${isInitialAuthor ? "text-green-200" : "text-gray-400"}`}>
                        {new Date(selectedTicket.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )
              })()}

              {/* Response Messages list */}
              {selectedTicket.responses &&
                selectedTicket.responses.map((response, rIdx) => {
                  const isAuthor = response.respondedBy?._id === user?._id || response.respondedBy === user?._id
                  const isSupport = ["admin", "superadmin", "support"].includes(response.respondedBy?.role)
                  
                  // Hide internal notes for regular client users
                  if (response.isInternal && isAgentOrUser) return null

                  return (
                    <div
                      key={rIdx}
                      className={`flex space-x-3 items-start max-w-lg ${
                        isAuthor ? "ml-auto flex-row-reverse space-x-reverse" : "mr-auto"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                          isSupport ? "bg-blue-600 text-white" : "bg-green-600 text-white"
                        }`}
                      >
                        {getInitials(response.respondedBy)}
                      </div>
                      <div
                        className={`rounded-2xl p-4 text-sm ${
                          response.isInternal
                            ? "bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 text-gray-900 dark:text-white"
                            : isAuthor
                              ? "bg-green-600 text-white"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                        }`}
                      >
                        {response.isInternal && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-800 mb-1">
                            INTERNAL NOTE
                          </span>
                        )}
                        <p className="whitespace-pre-line">{response.message}</p>

                        {/* Reply Attachments */}
                        {response.attachments && response.attachments.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className={`text-[10px] font-bold uppercase ${isAuthor ? "text-green-200" : "text-gray-400"}`}>
                              Attachments:
                            </p>
                            {response.attachments.map((file, fileIdx) => (
                              <a
                                key={fileIdx}
                                href={`${selectedTicket.url || API_BASE_URL}${file.url}`}
                                target="_blank"
                                rel="noreferrer"
                                className={`inline-flex items-center px-2 py-1 border rounded-lg text-xs mt-1 mr-1 ${
                                  isAuthor
                                    ? "bg-green-700 border-green-800 text-white hover:bg-green-800"
                                    : "bg-white dark:bg-gray-800 border-gray-250 dark:border-gray-600 text-green-600"
                                }`}
                              >
                                <DownloadIcon className="mr-1 w-3.5 h-3.5" /> {file.originalName}
                              </a>
                            ))}
                          </div>
                        )}
                        <span className={`text-[10px] block text-right mt-1.5 ${isAuthor ? "text-green-150" : "text-gray-400"}`}>
                          {new Date(response.respondedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )
                })}
              <div ref={messagesEndRef} />
            </div>

            {/* Closed Banner Warning */}
            {selectedTicket.status === "closed" ? (
              <div className="bg-red-50 dark:bg-red-950/20 border-t border-red-200 dark:border-red-900/50 px-6 py-4 text-center">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                  This ticket is closed. Please reopen or create a new ticket to continue communication.
                </p>
              </div>
            ) : (
              /* Message Input & Send box */
              <form onSubmit={handleReplySubmit} className="p-4 border-t border-gray-200 dark:border-gray-750 space-y-3 bg-gray-50 dark:bg-gray-850">
                {/* Reply File Upload list preview */}
                {replyFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    {replyFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
                        <ClipIcon className="w-3.5 h-3.5 mr-1 text-gray-500" />
                        <span className="truncate max-w-[120px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx, "reply")}
                          className="ml-1.5 text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  {/* File Selector */}
                  <label className="p-2 text-gray-500 dark:text-gray-400 hover:text-green-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl cursor-pointer transition">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileChange(e, "reply")}
                    />
                    <AttachIcon className="w-5 h-5" />
                  </label>

                  <textarea
                    rows={1}
                    placeholder="Type your message reply..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-650 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-green-500 text-sm focus:border-green-500"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />

                  {/* Internal Note Option for admin team */}
                  {(isAdmin || isSupportStaff) && (
                    <label className="flex items-center text-xs text-gray-500 dark:text-gray-400 cursor-pointer space-x-1.5">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                      />
                      <span>Internal Note</span>
                    </label>
                  )}

                  <button
                    type="submit"
                    disabled={sendingReply || (!replyText.trim() && replyFiles.length === 0)}
                    className="p-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition"
                  >
                    {sendingReply ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin block" />
                    ) : (
                      <SendIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* CREATE TICKET DRAWER FORM */}
      {showCreateModal &&
        createPortal(
          <div className="fixed inset-0 z-[9999] overflow-hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-gray-500/30 dark:bg-black/40 backdrop-blur-sm transition-opacity duration-300"
              onClick={() => setShowCreateModal(false)}
            ></div>

            <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
              <div className="w-screen max-w-md transform transition-transform duration-300 ease-in-out bg-white dark:bg-gray-800 shadow-2xl flex flex-col justify-between">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Support Ticket</h2>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  <form onSubmit={handleCreateTicketSubmit} className="space-y-4">
                    <div className="text-left">
                      <label className="text-xs text-gray-400 font-bold uppercase block">Subject</label>
                      <input
                        type="text"
                        required
                        placeholder="Summarize your issue..."
                        className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-green-500 focus:border-green-500 text-sm"
                        value={ticketSubject}
                        onChange={(e) => setTicketSubject(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-left">
                      <div>
                        <label className="text-xs text-gray-400 font-bold uppercase block">Category</label>
                        <select
                          className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-green-500 text-sm"
                          value={ticketCategory}
                          onChange={(e) => setTicketCategory(e.target.value)}
                        >
                          <option value="general">General Query</option>
                          <option value="technical">Technical Support</option>
                          <option value="billing">Billing Inquiry</option>
                          <option value="other">Other Issues</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 font-bold uppercase block">Priority</label>
                        <select
                          className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-green-500 text-sm"
                          value={ticketPriority}
                          onChange={(e) => setTicketPriority(e.target.value)}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="text-left">
                      <label className="text-xs text-gray-400 font-bold uppercase block">Description</label>
                      <textarea
                        required
                        rows={4}
                        placeholder="Describe your issue or query in details..."
                        className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-green-500 focus:border-green-500 text-sm"
                        value={ticketDescription}
                        onChange={(e) => setTicketDescription(e.target.value)}
                      />
                    </div>

                    {/* Attachment selector dropzone */}
                    <div className="text-left">
                      <label className="text-xs text-gray-400 font-bold uppercase block mb-1">Attachments</label>
                      <div className="border-2 border-dashed border-gray-350 dark:border-gray-650 rounded-xl p-4 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-750">
                        <ClipIcon className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          Drag files here or click to upload
                        </p>
                        <label className="mt-2.5 px-3 py-1.5 bg-white border border-gray-300 dark:border-gray-600 dark:bg-gray-800 text-xs font-bold rounded-lg text-gray-700 dark:text-gray-300 cursor-pointer shadow hover:bg-gray-50 dark:hover:bg-gray-750">
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "create")}
                          />
                          Choose Files
                        </label>
                      </div>

                      {/* Selected files preview */}
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-750 px-3 py-1.5 rounded-lg border border-gray-200/50">
                              <span className="truncate max-w-[200px]">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(idx, "create")}
                                className="text-red-500 hover:text-red-700"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 flex justify-end space-x-2 border-t border-gray-200 dark:border-gray-700 mt-6">
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(false)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition"
                      >
                        {submitting ? "Submitting..." : "Submit Ticket"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }
    </div>
  )
}

export default Support
